import { postGameByGameIdEndMutation } from "@aura-frontend/omnibus-az-client";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { Spinner } from "@/components/ui/spinner";

import GameSetup from "../../../GameSetup";
import { useGetActiveGame } from "../../../hooks/useGetActiveGame";
import { setSessionType } from "../../../store/slices/session";
import {
  setCurrentFilter,
  setTrackingState,
} from "../../../store/slices/socket";
import { setActiveView } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import { useUpdateGameState } from "../../../utils/useUpdateGameState";
import { resumeGameEffects } from "../../modals/ActiveGameModal/helpers";
import { GameView } from "./GameView";

/**
 * GameModeView - Main container for Game mode
 *
 * On mount, checks if there's an active game on this machine.
 * - If an active game exists, auto-resumes it (rehydrates Redux state).
 * - If no active game, shows the setup page.
 *
 * Switches between two views based on Redux state:
 * - GameSetup (RosterBuilder) - Team and lineup selection (areRostersOpen = true)
 * - GameView - Live game tagging interface (areRostersOpen = false)
 *
 * Also manages socket event filtering for game-specific data.
 */
const GameModeView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { fetchActiveGame } = useGetActiveGame();
  const updateGameState = useUpdateGameState();
  const [isCheckingActiveGame, setIsCheckingActiveGame] = useState(true);

  const isGameActive = useSelector(
    (state: RootState) => state.game.isGameActive,
  );

  // Check if rosters are still being set up
  const areRostersOpen = useSelector(
    (state: RootState) => state.game.areRostersOpen,
  );

  // On mount, check for an active game and auto-resume if found
  useEffect(() => {
    // Branch 1: Already active (resume path or localStorage)
    if (isGameActive) {
      setIsCheckingActiveGame(false);
      return;
    }

    // Branch 2: Navigated from Welcome — skip API check, show setup
    if ((location.state as { showSetup?: boolean })?.showSetup) {
      dispatch(setSessionType("game"));
      dispatch(setTrackingState(true));
      dispatch(setActiveView("Game Mode"));
      setIsCheckingActiveGame(false);
      // Clear state so page refresh does a full check
      window.history.replaceState({}, "");
      return;
    }

    // Branch 3: Direct navigation/refresh — full API check (existing logic)
    const checkAndResumeActiveGame = async () => {
      const activeGamePayload = await fetchActiveGame();

      if (activeGamePayload?.game?.type === "game") {
        resumeGameEffects(dispatch, {
          game: activeGamePayload.game,
          awayTeamPlayers: activeGamePayload.awayTeamPlayers,
          homeTeamPlayers: activeGamePayload.homeTeamPlayers,
        });
        updateGameState(activeGamePayload.gameState, activeGamePayload.game);
      } else {
        // End any non-game active session before setting up a new game
        if (activeGamePayload?.game) {
          try {
            const { mutationFn: endGameFn } = postGameByGameIdEndMutation();
            if (endGameFn)
              await endGameFn(
                {
                  body: {},
                  path: {
                    // @ts-expect-error gameUuid is not correctly typed in the API spec.
                    game_id: activeGamePayload.game.gameUuid,
                  },
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {} as any,
              );
          } catch {
            // If ending fails, still proceed to game setup
          }
        }

        dispatch(setSessionType("game"));
        dispatch(setTrackingState(true));
        dispatch(setActiveView("Game Mode"));
      }

      setIsCheckingActiveGame(false);
    };

    checkAndResumeActiveGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set socket filter to show only pitch data events during game
  useEffect(() => {
    dispatch(setCurrentFilter("has_pitch_data"));

    // Reset filter when leaving game view
    return () => {
      dispatch(setCurrentFilter("all"));
    };
  }, [dispatch]);

  if (isCheckingActiveGame) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner className="h-8 w-8" />
        <span className="ml-3 text-base text-muted-foreground">
          Loading game...
        </span>
      </div>
    );
  }

  return <>{areRostersOpen ? <GameSetup /> : <GameView />}</>;
};

export default GameModeView;
